/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Divider,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { buildFeatureSet, getCredentials } from '../../cache/auth'
import AdotelSkeleton from '../../components/AdotelSkeleton'
import PromotionSummary from '../../components/PromotionSummary'
import {
  BOOKING_DETAILS_QUERY,
  BOOKING_HISTORY_QUERY
} from '../../graphql/bookingDetail'
import { CHANGE_RESERVE_DETAILS_MUTATION } from '../../graphql/changeReserveDetails'
import { ACCOMMODATION_IMAGES_QUERY } from '../../graphql/upload'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { BookingDetail, BookingHistory } from '../../types/bookingDetails'
import { ChangeReserveDetailsInput } from '../../types/changeReserveDetails'
import { Image } from '../../types/image'
import { canViewBookingHistory, isForbidden } from './access'
import AddDescription from './components/AddDescription'
import AmountDetails from './components/AmountDetails'
import BillingSummary from './components/BillingSummary'
import BookerDetails from './components/BookerDetails'
import EditForm from './components/EditForm'
import HistoryTable from './components/HistoryTable'
import LastChanger from './components/LastChanger'
import ReservationNotes, { RoomStayNotes } from './components/ReservationNotes'
import ReserveDetails from './components/ReserveDetails'
import RoomDetails from './components/RoomDetails'
import SourceInfo from './components/SourceInfo'
import Status from './components/Status'
import Header from './header'
import useStyles from './styles/index.styles'

const BookingDetailsResults = () => {
  const classes = useStyles()
  const params = useParams<{ reserveNumber: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [page, setPage] = useState(1)
  const [perPage] = useState(10)
  const [adminEditMode, setAdminEditMode] = useState(false)
  const [isOpencheckStatusModal, setIsOpencheckStatusModal] = useState(false)
  const [bookingHistory, setBookingHistory] = useState<BookingDetail[]>([])
  // Hidden when the role lacks the feature, and also when the BFF still refuses it (a role
  // holding the feature without the matching IAM permission): no section, no error toast.
  const [historyForbidden, setHistoryForbidden] = useState(false)
  const showHistory =
    canViewBookingHistory(buildFeatureSet(getCredentials())) &&
    !historyForbidden
  const [bookingDetails, setBookingDetails] = useState<BookingDetail>()
  const bookingRooms = bookingDetails?.rooms
  const notesRoomNumbers = useMemo(
    () => bookingRooms?.map((room) => room.displayReservationNumber) || [],
    [bookingRooms]
  )
  const [changeReserveInput, setChangeReserveInput] =
    useState<ChangeReserveDetailsInput>({
      description: '',
      CRSStatus: 'Cancelled',
      PMSStatus: 'Cancelled',
      rooms: []
    })

  const [getAccommodationImages, { data: accoommodationImages }] =
    useLazyQuery<{
      accommodationImages: Image[]
    }>(ACCOMMODATION_IMAGES_QUERY, {
      fetchPolicy: 'network-only'
    })

  const [
    getBookingHistory,
    { data: bookingHistoryData, loading: loadingForBookingHistory }
  ] = useLazyQuery<{
    bookingHistory: BookingHistory
  }>(BOOKING_HISTORY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (res?.bookingHistory) {
        setBookingHistory((prevBookingHistory) => [
          ...prevBookingHistory,
          ...res.bookingHistory.bookingHistories
        ])
      }
    },
    // Without this a failed history request left the table silently empty, which read as
    // "this booking has no history" rather than "history could not be loaded".
    onError: (error) => {
      if (isForbidden(error)) {
        setHistoryForbidden(true)
        return
      }
      toast.error(error.message)
    }
  })

  const {
    data: bookingDetailData,
    loading: loadingForBookingDetails,
    refetch: refetchForBookingDetails
  } = useQuery<{
    bookingDetail: BookingDetail
  }>(BOOKING_DETAILS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      reservationNumber: params.reserveNumber
    },
    onCompleted: (res) => {
      setBookingDetails(res.bookingDetail)
      if (showHistory) {
        getBookingHistory({
          variables: {
            reservationNumber: params.reserveNumber,
            page: page,
            perPage: perPage
          }
        })
      }
      getAccommodationImages({
        variables: { propertyCode: res.bookingDetail.basicDetails.propertyCode }
      })

      setChangeReserveInput({
        CRSStatus: res.bookingDetail.basicDetails.crsStatus,
        PMSStatus: res.bookingDetail.basicDetails.pmsStatus,
        description: '',
        rooms: res.bookingDetail.rooms.map((room) => ({
          roomCRSStatus: room.crsStatus,
          roomPMSStatus: room.pmsStatus,
          roomReservationNumber: room.reservationNumber,
          penaltyAmount: 0
        }))
      })
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const [ChangeReserveDetails, { loading: loadingForChangeReserveDetails }] =
    useMutation(CHANGE_RESERVE_DETAILS_MUTATION, {
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        toast.success(response.changeReserveDetails.message)
        refetchForBookingDetails()
        setAdminEditMode(false)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  useEffect(() => {
    if (showHistory && page !== 1 && !loadingForBookingHistory) {
      getBookingHistory({
        variables: {
          reservationNumber: params.reserveNumber,
          page: page,
          perPage: perPage
        }
      })
    }
  }, [page])

  const currentStatusId = useMemo(() => {
    return bookingDetailData?.bookingDetail.basicDetails.id
  }, [bookingDetailData])

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  return (
    <div className={classes.root}>
      <Header
        setAdminEditMode={setAdminEditMode}
        adminEditMode={adminEditMode}
        data={bookingDetailData?.bookingDetail}
        bookingDetail={bookingDetails}
        isOpencheckStatusModal={isOpencheckStatusModal}
        setIsOpencheckStatusModal={setIsOpencheckStatusModal}
      />
      <div className={classes.basicDetails}>
        <div className={classes.left} data-testid="booking-details-left-column">
          <ReserveDetails
            data={bookingDetails?.basicDetails}
            accommodationImages={accoommodationImages?.accommodationImages}
            adminEditMode={adminEditMode}
          />
          <BookerDetails
            bookerInfo={bookingDetails?.bookerInfo}
            adminEditMode={adminEditMode}
          />
          <div className={classes.lastChangerSourceInfo}>
            <LastChanger
              data={bookingDetails?.lastChanger}
              adminEditMode={adminEditMode}
            />
            <SourceInfo
              data={bookingDetails?.sourceInfo}
              adminEditMode={adminEditMode}
            />
          </div>
        </div>
        <div
          className={classes.right}
          data-testid="booking-details-right-column">
          <Status
            data={bookingDetails}
            originalData={bookingDetailData?.bookingDetail}
            adminEditMode={adminEditMode}
            bookingHistories={bookingHistory}
            page={page}
            perPage={perPage}
            total={bookingHistoryData?.bookingHistory.total || 0}
            setPage={setPage}
            currentStatusId={currentStatusId || ''}
            bookingId={bookingDetails?.basicDetails.id || ''}
            setBookingDetail={setBookingDetails}
          />
          {/* What the promotion did, beside what it cost. BillingSummary below totals the
              discount; this names each promotion, its amount, and the room stay it landed
              on — the breakdown wbs-ng has shown a booker all along while the agent acting
              for them saw only a total. */}
          <PromotionSummary
            application={bookingDetailData?.bookingDetail.promotionApplication}
          />
          <BillingSummary
            adminEditMode={adminEditMode}
            billingInfo={bookingDetailData?.bookingDetail.billingSummary}
            promotionApplication={
              bookingDetailData?.bookingDetail.promotionApplication
            }
            rooms={bookingDetails?.rooms}
          />
        </div>
      </div>
      {isRoleAdmin && bookingDetails?.failedReason && (
        <div className={classes.error}>
          <Typography variant="subtitle1" className={classes.verticalAlign}>
            <WarningIcon style={{ fontSize: 16, marginRight: 6 }} /> Failed
          </Typography>
          <Typography variant="h6">{bookingDetails?.failedReason}</Typography>
        </div>
      )}
      {adminEditMode && (
        <EditForm
          title={getTranslate('Edit Booking details')}
          changeReserveInput={changeReserveInput}
          setChangeReserveInput={setChangeReserveInput}
          roomIndex={-1}
          reservationNumber={params.reserveNumber}
        />
      )}
      <ReservationNotes
        reservationNumber={params.reserveNumber}
        roomNumbers={notesRoomNumbers}>
        {bookingDetails?.rooms.map((room, i) => (
          <div key={room.reservationNumber}>
            <RoomDetails
              room={room}
              currency={bookingDetails?.basicDetails.currency}
              adminEditMode={adminEditMode}
              index={i + 1}>
              <RoomStayNotes roomNumber={room.displayReservationNumber} />
            </RoomDetails>
            {adminEditMode && (
              <EditForm
                title={
                  lang === 'fa'
                    ? `${getTranslate('Edit')} ${getTranslate(
                        'Details'
                      )} ${getTranslate('Room')}  ${i + 1}`
                    : `${getTranslate('Edit')} ${getTranslate('Room')} ${
                        i + 1
                      } ${getTranslate('Details')}`
                }
                changeReserveInput={changeReserveInput}
                setChangeReserveInput={setChangeReserveInput}
                customizedForRoom
                roomIndex={i}
                reservationNumber={room.reservationNumber}
              />
            )}
          </div>
        ))}
      </ReservationNotes>
      <AmountDetails
        curruncy={bookingDetails?.basicDetails.currency}
        roomsPriceDetails={bookingDetails?.roomsPriceDetails}
      />
      {!isMobile && showHistory && (
        <HistoryTable
          bookingHistories={bookingHistory || []}
          loadingForBookingHistory={loadingForBookingHistory}
          page={page}
          setPage={setPage}
          total={bookingHistoryData?.bookingHistory.total ?? 0}
          perPage={perPage}
          adminEditMode={adminEditMode}
        />
      )}

      {adminEditMode && (
        <>
          <AddDescription setChangeReserveInput={setChangeReserveInput} />
          <Divider className={classes.divider} />
          <div className={classes.btnsContainer}>
            <Button
              variant="text"
              className={classes.button}
              onClick={() => {
                setAdminEditMode(false)
              }}>
              <Typography variant="h5">{getTranslate('Cancel')} </Typography>
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => {
                ChangeReserveDetails({
                  variables: {
                    reservationNumber: params.reserveNumber,
                    input: changeReserveInput
                  }
                })
              }}>
              <Typography variant="h5" className={classes.btnText}>
                {getTranslate('Save Changes')}
              </Typography>
            </Button>
          </div>
        </>
      )}
      <AdotelSkeleton
        open={
          loadingForBookingDetails ||
          // loadingForBookingHistory ||
          loadingForChangeReserveDetails
        }
        numberOfCircles={4}
        circlesColor={theme.palette.primary.main}
      />
    </div>
  )
}

export default BookingDetailsResults
